import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { BrainCircuit, FileClock, Layers3, MessagesSquare, Repeat2, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { usePatients } from "@/lib/usePatients";
import { PageHeader } from "@/components/clinical/PageHeader";
import { ReviewNote } from "@/components/clinical/ReviewNote";
import { EmptyState } from "@/components/clinical/EmptyState";
import { IntegratedClinicalConsole } from "@/components/clinical/IntegratedClinicalConsole";
import { LocalClinicalCopilotPanel } from "@/components/clinical/LocalClinicalCopilotPanel";
import { RepeatDoseAnalysisPanel } from "@/components/clinical/RepeatDoseAnalysisPanel";
import { ClinicalTextInterpretationPanel } from "@/components/clinical/ClinicalTextInterpretationPanel";
import { ClinicalAnalysisSnapshotPanel } from "@/components/clinical/ClinicalAnalysisSnapshotPanel";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const PANEL_DEFINITIONS = [
  { id: "integrated", label: "Visão integrada", shortLabel: "Visão geral", icon: Layers3 },
  { id: "texts", label: "Textos e estado clínico", shortLabel: "Textos clínicos", icon: MessagesSquare },
  { id: "repeat", label: "Redose e sobreposição", shortLabel: "Redoses", icon: Repeat2 },
  { id: "copilot", label: "Hipóteses e manejo", shortLabel: "Hipóteses", icon: BrainCircuit },
  { id: "snapshots", label: "Análises salvas", shortLabel: "Análises salvas", icon: Save },
] as const;

export default function ClinicalIntelligence() {
  const { data: patients = [] } = usePatients();
  const [searchParams, setSearchParams] = useSearchParams();
  const [patientId, setPatientId] = useState(() => searchParams.get("patient") ?? localStorage.getItem("clinicalos-my-space-patient") ?? "");
  const [periodDays, setPeriodDays] = useState(30);
  const [activePanel, setActivePanel] = useState("integrated");
  const sinceIso = useMemo(() => new Date(Date.now() - periodDays * 24 * 3600_000).toISOString(), [periodDays]);

  useEffect(() => {
    if (!patientId || !patients.length) return;
    if (!patients.some((patient: any) => patient.id === patientId)) setPatientId("");
  }, [patientId, patients]);

  function choosePatient(nextPatientId: string) {
    setPatientId(nextPatientId);
    setActivePanel("integrated");
    if (nextPatientId) {
      localStorage.setItem("clinicalos-my-space-patient", nextPatientId);
      setSearchParams({ patient: nextPatientId });
    } else {
      setSearchParams({});
    }
  }

  const { data: patient } = useQuery({
    queryKey: ["ci-p", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await supabase.from("patients").select("*").eq("id", patientId).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  const { data: medications = [], refetch: refetchMedications } = useQuery({
    queryKey: ["ci-meds", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patient_medications")
        .select("*, substances(*), substance_formulations(*)")
        .eq("patient_id", patientId)
        .is("deleted_at", null);
      if (error) throw error;
      return data ?? [];
    },
  });
  const { data: sessions = [] } = useQuery({
    queryKey: ["ci-sessions", patientId, periodDays],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clinical_sessions")
        .select("*")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .gte("session_at", sinceIso)
        .order("session_at", { ascending: false })
        .limit(120);
      if (error) throw error;
      return data ?? [];
    },
  });
  const { data: doses = [], refetch: refetchDoses } = useQuery({
    queryKey: ["ci-doses", patientId, periodDays],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("medication_dose_logs")
        .select("*, substances(*), substance_formulations(*)")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .gte("actual_time", sinceIso)
        .order("actual_time", { ascending: false })
        .limit(600);
      if (error) throw error;
      return data ?? [];
    },
  });
  const { data: checkins = [] } = useQuery({
    queryKey: ["ci-checkins", patientId, periodDays],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("session_checkins")
        .select("*")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .gte("checkin_at", sinceIso)
        .order("checkin_at", { ascending: false })
        .limit(600);
      if (error) throw error;
      return data ?? [];
    },
  });
  const { data: subUse = [] } = useQuery({
    queryKey: ["ci-subuse", patientId, periodDays],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("substance_use_logs")
        .select("*")
        .eq("patient_id", patientId)
        .gte("used_at", sinceIso)
        .order("used_at", { ascending: false })
        .limit(300);
      if (error) throw error;
      return data ?? [];
    },
  });
  const { data: inventory = [] } = useQuery({
    queryKey: ["ci-inv", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("medication_inventory")
        .select("*")
        .eq("patient_id", patientId);
      if (error) throw error;
      return data ?? [];
    },
  });
  const { data: targetSymptoms = [] } = useQuery({
    queryKey: ["ci-target-symptoms", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patient_target_symptoms")
        .select("*")
        .eq("patient_id", patientId)
        .eq("status", "ativo")
        .is("deleted_at", null)
        .order("priority", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
  const { data: symptomMeasurements = [] } = useQuery({
    queryKey: ["ci-symptom-measurements", patientId, periodDays],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("symptom_measurements")
        .select("*")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .gte("measured_at", sinceIso)
        .order("measured_at", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  function refresh() {
    void refetchDoses();
    void refetchMedications();
  }

  const panelSubtitle = useMemo(() => {
    switch (activePanel) {
      case "texts": return `${sessions.length} sessão(ões), ${checkins.length} check-in(s) e ${doses.length} dose(s) disponíveis para interpretação textual.`;
      case "repeat": return `${doses.length} registro(s) de dose na janela; classificação não equivale automaticamente a potencialização ou abuso.`;
      case "copilot": return "Hipóteses, explicações alternativas, contradições, confiança e próximos dados úteis.";
      case "snapshots": return "Persistência versionada da análise para revisão médica e comparação longitudinal.";
      default: return `${medications.length} tratamento(s), ${doses.length} dose(s), ${checkins.length} check-in(s) e ${targetSymptoms.length} sintoma(s)-alvo.`;
    }
  }, [activePanel, checkins.length, doses.length, medications.length, sessions.length, targetSymptoms.length]);

  return (
    <div className="min-w-0 space-y-6">
      <PageHeader title="Inteligência clínica" description="Uma central de revisão longitudinal. Escolha um módulo por vez; cada painel mantém separados os dados observados, as hipóteses e as incertezas." />
      <ReviewNote>O sistema produz apoio à revisão médica. Não diagnostica autonomamente, não prescreve e não transforma associação temporal em causalidade. Cada hipótese mantém fontes, negações, contradições, incerteza e próximo dado útil.</ReviewNote>
      <Card className="grid min-w-0 gap-3 p-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
        <div className="min-w-0">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Paciente</Label>
          <select className="mt-1 h-11 w-full min-w-0 rounded-md border border-input bg-background px-3 text-base sm:h-10 sm:text-sm" value={patientId} onChange={(event) => choosePatient(event.target.value)}>
            <option value="">Selecione…</option>
            {patients.map((item: any) => <option key={item.id} value={item.id}>{item.full_name}</option>)}
          </select>
        </div>
        <div className="min-w-0">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Janela</Label>
          <select className="mt-1 h-11 w-full min-w-0 rounded-md border border-input bg-background px-3 text-base sm:h-10 sm:w-auto sm:text-sm" value={periodDays} onChange={(event) => setPeriodDays(Number(event.target.value))}>
            {[1, 3, 7, 14, 30, 60, 90].map((days) => <option key={days} value={days}>{days === 1 ? "24h" : `${days} dias`}</option>)}
          </select>
        </div>
      </Card>

      {!patientId ? (
        <Card className="p-6">
          <EmptyState icon={<BrainCircuit className="h-5 w-5" />} title="Selecione um paciente" description="A inteligência clínica cruza cadastro, textos, tratamento, doses, contexto, check-ins e sintomas-alvo. Sem dados, apresenta lacunas em vez de inventar conclusões." />
        </Card>
      ) : (
        <Tabs value={activePanel} onValueChange={setActivePanel} className="min-w-0 space-y-4">
          <Card className="min-w-0 space-y-3 rounded-2xl border-primary/15 p-3 sm:p-4">
            <div className="flex min-w-0 items-start gap-2">
              <FileClock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div className="min-w-0">
                <div className="text-sm font-medium">{PANEL_DEFINITIONS.find((panel) => panel.id === activePanel)?.label}</div>
                <div className="break-words text-xs text-muted-foreground">{panelSubtitle}</div>
              </div>
            </div>
            <Label htmlFor="clinical-intelligence-module" className="block text-[10px] uppercase tracking-wide text-muted-foreground sm:hidden">Módulo da inteligência</Label>
            <select id="clinical-intelligence-module" className="h-11 w-full rounded-md border border-input bg-background px-3 text-base sm:hidden" value={activePanel} onChange={(event) => setActivePanel(event.target.value)}>
              {PANEL_DEFINITIONS.map((panel) => <option key={panel.id} value={panel.id}>{panel.label}</option>)}
            </select>
            <div className="hidden min-w-0 sm:block">
              <TabsList className="grid h-auto w-full grid-cols-2 gap-2 bg-transparent p-0 md:grid-cols-5">
                {PANEL_DEFINITIONS.map((panel) => {
                  const Icon = panel.icon;
                  return <TabsTrigger key={panel.id} value={panel.id} className="min-h-11 h-auto min-w-0 justify-start gap-2 whitespace-normal rounded-xl border border-transparent px-3 py-2.5 text-left text-xs leading-snug data-[state=active]:border-primary/20 data-[state=active]:bg-primary/5"><Icon className="h-4 w-4 shrink-0" /><span>{panel.shortLabel}</span></TabsTrigger>;
                })}
              </TabsList>
            </div>
          </Card>

          <TabsContent value="integrated" className="mt-0 min-w-0">
            <IntegratedClinicalConsole
              patient={patient}
              medications={medications as any}
              doses={doses as any}
              checkins={checkins as any}
              sessions={sessions as any}
              substanceUse={subUse as any}
              inventory={inventory as any}
              periodDays={periodDays}
              targetSymptoms={targetSymptoms as any}
              symptomMeasurements={symptomMeasurements as any}
              onDoseChanged={refresh}
            />
          </TabsContent>

          <TabsContent value="texts" className="mt-0 min-w-0">
            <ClinicalTextInterpretationPanel patient={patient} sessions={sessions as any} checkins={checkins as any} doses={doses as any} />
          </TabsContent>

          <TabsContent value="repeat" className="mt-0 min-w-0">
            <RepeatDoseAnalysisPanel doses={doses as any} />
          </TabsContent>

          <TabsContent value="copilot" className="mt-0 min-w-0">
            <LocalClinicalCopilotPanel
              patient={patient}
              medications={medications as any}
              doses={doses as any}
              checkins={checkins as any}
              sessions={sessions as any}
              substanceUse={subUse as any}
              inventory={inventory as any}
              periodDays={periodDays}
              targetSymptoms={targetSymptoms as any}
              symptomMeasurements={symptomMeasurements as any}
            />
          </TabsContent>

          <TabsContent value="snapshots" className="mt-0 min-w-0">
            <ClinicalAnalysisSnapshotPanel
              patient={patient}
              medications={medications as any}
              doses={doses as any}
              checkins={checkins as any}
              sessions={sessions as any}
              substanceUse={subUse as any}
              inventory={inventory as any}
              periodDays={periodDays}
              targetSymptoms={targetSymptoms as any}
              symptomMeasurements={symptomMeasurements as any}
            />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
